import { useState } from 'react';
import type { CancellationState } from '@localagent/sdk';

interface CancellationSectionProps {
  cancellationState?: CancellationState | null;
  onCancel: (reason?: string) => void | Promise<void>;
  onReactivate: () => void | Promise<void>;
  isCancelling: boolean;
  isReactivating: boolean;
  /** When true, hide the cancel control (non-owner presentation). */
  canCancel?: boolean;
  isTrial?: boolean;
  /** Period end / trial end shown in the confirm dialog before cancel is accepted. */
  previewEffectiveAt?: string | null;
}

export function CancellationSection({
  cancellationState,
  onCancel,
  onReactivate,
  isCancelling,
  isReactivating,
  canCancel = true,
  isTrial = false,
  previewEffectiveAt = null,
}: CancellationSectionProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  const isPending = cancellationState?.is_pending ?? false;
  const canReactivate = cancellationState?.can_reactivate ?? false;
  const dialogEffectiveAt =
    cancellationState?.effective_at ?? previewEffectiveAt ?? null;

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleCancelClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmCancel = async () => {
    try {
      await onCancel(cancellationReason || undefined);
    } catch {
      // Keep the dialog open so the user can retry; mutation error surfaces via
      // isCancelling clearing without a pending banner.
      return;
    }
    setShowConfirmDialog(false);
    setCancellationReason('');
    setAcknowledged(false);
  };

  const handleDismiss = () => {
    setShowConfirmDialog(false);
    setCancellationReason('');
    setAcknowledged(false);
  };

  if (isPending && cancellationState) {
    return (
      <div
        className="rounded-lg bg-warning-subtle p-4 space-y-3"
        data-testid="billing-cancel-pending"
      >
        <div className="flex items-center gap-2">
          <svg
            className="h-5 w-5 text-warning"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h3 className="text-label font-medium text-warning-text">
            Cancellation Pending
          </h3>
        </div>

        <p className="text-body text-muted-foreground">
          Your subscription will end on{' '}
          <span className="font-medium" data-testid="billing-cancel-effective-date">
            {formatDate(cancellationState.effective_at)}
          </span>
          . You&apos;ll continue to have access until then. Remaining credits are
          forfeited at that date, and the current period is not refunded.
        </p>

        {canReactivate && (
          <button
            onClick={onReactivate}
            disabled={isReactivating}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-label font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="billing-reactivate"
          >
            {isReactivating ? 'Reactivating...' : 'Keep My Subscription'}
          </button>
        )}
      </div>
    );
  }

  if (!canCancel) {
    return null;
  }

  return (
    <>
      <div className="rounded-lg bg-card p-4 space-y-3" data-testid="cancellation-section">
        <div>
          <h3 className="text-label font-medium text-muted-foreground">Cancel Subscription</h3>
          <p className="text-caption text-muted-foreground mt-1">
            Your subscription will remain active until the end of the current billing period.
          </p>
        </div>

        <button
          onClick={handleCancelClick}
          disabled={isCancelling}
          className="inline-flex items-center justify-center rounded-md px-4 py-2 text-label font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="billing-cancel-plan"
        >
          {isCancelling ? 'Cancelling...' : 'Cancel Subscription'}
        </button>
      </div>

      {showConfirmDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          data-testid="billing-cancel-dialog"
        >
          <div className="bg-background border rounded-lg p-6 max-w-md w-full mx-4 space-y-4">
            <h3 className="text-title-2 font-semibold">Cancel Subscription?</h3>

            <p className="text-body text-muted-foreground">
              {isTrial ? (
                <>
                  Your trial ends on{' '}
                  <span className="font-medium" data-testid="billing-cancel-effective-date">
                    {formatDate(dialogEffectiveAt)}
                  </span>
                  . No charge will be made. After that date you lose access to the product.
                </>
              ) : (
                <>
                  Your subscription remains active until{' '}
                  <span className="font-medium" data-testid="billing-cancel-effective-date">
                    {formatDate(dialogEffectiveAt)}
                  </span>
                  . Remaining credits are forfeited at that date, you lose access afterward,
                  and the current period is not refunded.
                </>
              )}
            </p>

            <div className="space-y-2">
              <label
                htmlFor="cancel-reason"
                className="text-label font-medium text-muted-foreground"
              >
                Reason for cancelling (optional)
              </label>
              <textarea
                id="cancel-reason"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Tell us why you're leaving..."
                className="w-full rounded-md bg-muted px-3 py-2 text-body placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                rows={3}
                data-testid="cancel-reason-input"
              />
            </div>

            <label className="flex items-start gap-2 text-body text-muted-foreground">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-1"
                data-testid="billing-cancel-acknowledge"
              />
              <span>
                I understand credits are forfeited, access ends on the effective date, and
                there is no refund for the current period.
              </span>
            </label>

            <div className="flex justify-end gap-3">
              <button
                onClick={handleDismiss}
                className="inline-flex items-center justify-center rounded-md bg-secondary px-4 py-2 text-label font-medium text-secondary-foreground hover:bg-secondary/80"
                data-testid="cancel-dialog-dismiss"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleConfirmCancel}
                disabled={isCancelling || !acknowledged}
                className="inline-flex items-center justify-center rounded-md bg-destructive px-4 py-2 text-label font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
                data-testid="billing-cancel-confirm"
              >
                {isCancelling ? 'Cancelling...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
